// React Imports
import React, { useState, useEffect, useContext } from 'react';

// Component Imports
import { AuthContext, ViewContext, usePlans, useNavigate } from 'components/lib';
import { useLocation } from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';

// Module Imports
import axios from 'axios';

// MUI Imports
import {
    Box,
    Typography,
    Button,
    Container,
    Card,
    CardContent,
    CardActions,
    Grid,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    createTheme,
    ThemeProvider,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { Delete } from '@mui/icons-material';
import { useAPI } from 'components/lib';

// Custom theme based on your color palette
const theme = createTheme({
    palette: {
        primary: {
            main: '#1450a0', // c-aivagam-blue
            light: '#1B6EDA', // c-aivagam-blue-light
            dark: '#0b2e5b', // c-aivagam-blue-500
        },
        secondary: {
            main: '#004AAD', // c-topgrep-blue
            light: '#3974c1', // c-topgrep-blue-hover
        },
        success: {
            main: '#008037', // c-topgrep-green
            light: '#39a96a', // c-topgrep-green-hover
        },
        warning: {
            main: '#FFCE4E', // c-topgrep-yellow
            light: '#fff9c4', // c-topgrep-yellow-hover
        },
        error: {
            main: '#FF1616', // c-topgrep-red
            light: 'rgb(255, 80, 80)', // c-topgrep-red-hover
        },
    },
});

export function Upgrade(props) {

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [couponData, setCouponData] = useState({
        code: '',
        isApplied: false,
    });

    const navigate = useNavigate();
    const location = useLocation();

    // context/data
    const context = useContext(AuthContext);
    const viewContext = useContext(ViewContext);
    const plans = useAPI('/api/account/plans/available');
    const currentPlan = context?.user?.plan || 'free';
    const currencySymbol = context?.user.currencySymbol;

    // Get all plans including free
    const allPlans = plans?.data?.plans
    const [displayPlans, setDisplayPlans] = useState([]);
    
    useEffect(() => {
        if (allPlans) {
            const upgradePlans = allPlans.filter(plan => plan.product_id !== 'enterprise')
            setDisplayPlans(upgradePlans)
        }
    }, [allPlans])

    useEffect(() => {
        if (allPlans) {
            const qs = location.search;
            let initialPlan = qs.substring(qs.indexOf('?plan=') + 6);

            // If no plan in URL or plan not found, default to first non-free plan
            if (!initialPlan || !displayPlans?.find(p => p.value === initialPlan)) {
                const nonFreePlans = displayPlans?.filter(p => p.product_id !== 'free');
                initialPlan = nonFreePlans?.length > 0 ? nonFreePlans[0].product_id : 'free';
            }
            setSelectedPlan(initialPlan);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayPlans, location.search]);


    // Handle plan selection and checkout
    const handleUpgrade = async (planValue) => {
        if (!planValue) return;
        setLoading(true);
        // If selecting free plan and already on free, just navigate to dashboard
        if (planValue === 'free' && currentPlan === 'free') {
            navigate('/dashboard');
            return;
        }

        try {
            const response = await axios.post('/api/account/plan', {
                plan: planValue,
                ...(couponData.isApplied && {couponCode: couponData.code})
            });

            if (response.data?.checkout_url) {
                window.location.href = response.data.checkout_url;
            }
        } catch (error) {
            setLoading(false);
            viewContext.notification.show(`${error?.response?.data?.error}` || error?.response?.data || props.t('account.upgrade.form.upgradeError'), 'error');
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponData.code.trim()) {
            viewContext.notification.show(
                viewContext.t('Components.Checkout.coupon.empty'),
                'warning', true
            );
            return;
        }

        setLoading(true);

        try {
            const options = {
                method: 'POST',
                url: `api/coupons/verify`,
                headers: {
                    accept: 'application/json',
                },
                data: {
                    items: [selectedPlan],
                    couponCode: couponData.code.trim(),
                    type: 'account',
                },
            };

            const response = await axios.request(options);

            if (response.data?.valid) {
                const { discountAmount, finalAmount, applicableItems } = response.data;
                setCouponData((prev) => ({
                    ...prev,
                    isApplied: true,
                    couponDiscount: discountAmount,
                    finalAmount: finalAmount,
                    items: applicableItems,
                }));
                viewContext.notification.show(
                    viewContext.t('Components.Checkout.coupon.success', { code: couponData.code }),
                    'success',
                    true,
                );
            } else {
                viewContext.notification.show(
                    viewContext.t('Components.Checkout.coupon.invalid'),
                    'error', true
                );
            }
        } catch (err) {
            if (err.response?.data?.error) {
                viewContext.handleError(err.response.data.error);
            } else {
                viewContext.handleError(err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveCoupon = () => {
        setCouponData({ code: '', isApplied: false });
        viewContext.notification.show(viewContext.t('Components.Checkout.coupon.removed'), 'info', true);
    };

    useEffect(() => {
        if (couponData?.isApplied) handleRemoveCoupon();
    }, [selectedPlan]);

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 6, px: 2, borderRadius: '8px' }}>
                {plans.loading || loading ? (
                    <LoadingBackdrop isOpen={plans.loading || loading} />
                ) : (
                    <Container maxWidth="lg">
                        {/* Header */}
                        <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ color: '#1450a0' }} gutterBottom>
                                {props.t('account.upgrade.form.title')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {props.t('account.upgrade.form.subtitle')}
                            </Typography>
                        </Box>

                        <Alert
                            sx={{
                                borderRadius: '8px',
                                marginBottom: '30px',
                                '& .MuiAlert-message':{
                                    overflow:'hidden'
                                }
                            }}
                            severity='info'
                        >
                        <p style={{marginBottom:'10px',paddingRight: '10px'}}>
                                {viewContext.t('Components.Checkout.coupon.prompt')}
                            </p>
                            <div style={{
                                display: 'flex',
                                gap: '1em',
                                width: '100%',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginBottom: '1em'
                            }}>
                                <TextField
                                    id='couponInput'
                                    variant='outlined'
                                    fullWidth
                                    placeholder='Enter Coupon Code'
                                    value={couponData.code}
                                    onChange={(e) => {
                                        setCouponData((prev) => ({ ...prev, code: e.target.value }))
                                    }}
                                    disabled={couponData.isApplied}
                                    InputProps={{
                                        endAdornment: (couponData.code && !couponData.isApplied) && (
                                            <InputAdornment position='end' sx={{ mr: '4px' }}>
                                                <IconButton
                                                    title='clear'
                                                    size='small'
                                                    onClick={() => {
                                                        setCouponData((prev) => ({ ...prev, code: '' }))
                                                    }}
                                                >
                                                    <ClearIcon fontSize='small' />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        backgroundColor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            height: '40px',
                                            '& .input': {
                                                height: '40px',
                                            }
                                        }
                                    }}
                                />
                                <AivagamButton
                                    type='action'
                                    buttonText={couponData.isApplied ? 'Applied' : 'Apply'}
                                    disabled={!couponData.code || couponData.isApplied}
                                    onClick={handleApplyCoupon}
                                    classStyles={{
                                        boxShadow: '0 4px 6px -2px rgba(0, 0,0,0.2)',
                                        width: '8em',
                                        borderRadius: '8px',
                                        fontWeight: 'bold'
                                    }}
                                />
                                {/* Render remove coupon only if coupon is applied */}
                                {!!couponData.isApplied && (
                                    <IconButton
                                        size='medium'
                                        onClick={handleRemoveCoupon}
                                        sx={{
                                            backgroundColor: '#f6e4e6ff',
                                            '&:hover': {
                                                backgroundColor: '#f6d6d6ff',
                                            },
                                            borderRadius: '8px'
                                        }}
                                    >
                                        <Delete fontSize='inherit' sx={{ color: 'darkred' }} />
                                    </IconButton>
                                )}
                            </div>
                        </Alert>

                        {/* Plans Grid */}
                        <Grid container spacing={3} justifyContent={displayPlans?.length < 3 ? 'center' : 'space-between'}>
                            {displayPlans?.map((plan, index) => {
                                const isFree = plan.product_id === 'free';
                                const isCurrentPlan = currentPlan === plan.product_id;
                                const isSelected = selectedPlan === plan.product_id;
                                const isDisabled = isFree && currentPlan !== 'free';
                                const isContact = plan.meta_data.payment_method === 'external';
                                const isCouponApplied = (couponData.isApplied && couponData?.items?.includes(plan.product_id))

                                // Button text logic
                                let buttonText = props.t('account.upgrade.form.button.upgrade');
                                if (isFree) {
                                    buttonText = isCurrentPlan ? props.t('account.upgrade.form.button.stayOnFree') : props.t('account.upgrade.form.button.freePlan');
                                } else if (isCurrentPlan) {
                                    buttonText = props.t('account.upgrade.form.button.currentPlan');
                                } else if (isContact) {
                                    buttonText = props.t('account.upgrade.form.button.contact')
                                }

                                return (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                position: 'relative',
                                                transition: 'all 0.3s',
                                                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                                boxShadow: isSelected ? 8 : 3,
                                                borderRadius: '16px',
                                                border: isSelected ? `1.5px solid ${theme.palette.primary.main}` : isCurrentPlan ? `1px solid ${theme.palette.primary.main}` : `1px solid #e0e0e0`,
                                                opacity: isDisabled ? 0.7 : 1,
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                '&:hover': {
                                                    boxShadow: isDisabled ? 3 : 6,
                                                }
                                            }}
                                            onClick={() => !isDisabled && setSelectedPlan(plan.product_id)}
                                        >
                                            {/* Popular Badge */}
                                            {(plan?.meta_data?.popular || isCurrentPlan) && (
                                                <Chip
                                                    icon={isCurrentPlan ? <CheckCircleIcon sx={{ color: 'inherit' }} /> : <StarIcon sx={{ color: 'inherit' }} />}
                                                    label={isCurrentPlan ? props.t('account.upgrade.form.chip.currentPlan') : props.t('account.upgrade.form.chip.mostPopular')}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 16,
                                                        right: 0,
                                                        borderTopRightRadius: 0,
                                                        borderBottomRightRadius: 0,
                                                        pl: 1,
                                                        fontWeight: 'bold',
                                                        color: isCurrentPlan ? 'white' : 'rgba(0,0,0,0.7)',
                                                        '& .MuiChip-icon': {
                                                            color: isCurrentPlan ? 'white' : 'rgba(0,0,0,0.7)',
                                                        },
                                                        fontSize: '0.75rem',
                                                        background: isCurrentPlan
                                                            ? 'linear-gradient(135deg, #1B6EDA 0%, #1450a0 100%)'
                                                            : 'linear-gradient(135deg, #FFD600 0%, #FFA000 100%)',
                                                    }}
                                                />
                                            )}

                                            <CardContent sx={{ pt: 2, pb: 1, flexGrow: 1 }}>
                                                <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                                                    {plan?.meta_data.name}
                                                </Typography>

                                                <Box sx={{ mb: (isCouponApplied) ? 1 : 2 }}>

                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            textDecoration: plan.actual_price ? 'line-through' : 'none',
                                                            color: 'text.disabled',
                                                            fontWeight: 'medium',
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        {plan.actual_price
                                                            ? `${currencySymbol}${plan.actual_price}`
                                                            : 'Try for Free'}
                                                    </Typography>

                                                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                                        <Typography variant="h4" component="span" fontWeight="bold">
                                                            {currencySymbol}{isCouponApplied ? couponData?.finalAmount : plan.price}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                                                            {plan.interval ? `/${plan.interval}M` : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                { isCouponApplied &&
                                                    <Typography variant='body2' color="text.secondary" sx={{ mb: 0.5 }}>
                                                        {`DISCOUNT(${couponData.code}) - ${currencySymbol}${couponData.couponDiscount}`}
                                                    </Typography>}


                                                <CardActions sx={{ p: 2, pt: 0, pb: isCouponApplied ? 1 : 2 }}>
                                                    <Button
                                                        fullWidth
                                                        variant={isSelected ? "contained" : "outlined"}
                                                        color="success"
                                                        size="medium"
                                                        disabled={isDisabled || (isCurrentPlan && !isFree) || !isSelected}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!isDisabled && !(isCurrentPlan && !isFree) && !isContact) {
                                                                handleUpgrade(plan.product_id)
                                                            } else if (isContact) {
                                                                navigate('/contact')
                                                            }
                                                        }}
                                                        sx={{
                                                            fontWeight: 500,
                                                            borderRadius: '16px',
                                                            color: isSelected ? 'white' : theme.palette.success.main,
                                                            '&.Mui-disabled': {
                                                                opacity: isDisabled ? 0.5 : 1,
                                                                ...(!isDisabled && !(isCurrentPlan && !isFree) && {
                                                                    color : theme.palette.success.main,
                                                                    borderColor: theme.palette.success.main
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        {buttonText}
                                                    </Button>
                                                </CardActions>

                                                <Divider sx={{ my: 2 }} />

                                                <List disablePadding>
                                                    {plan?.meta_data?.features?.map((feature, idx) => (
                                                        <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={feature?.name}
                                                                primaryTypographyProps={{
                                                                    variant: 'body2',
                                                                    color: 'text.secondary',
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Container>
                )}
            </Box>
        </ThemeProvider>
    );
}